import { CompactPagination } from "@/components/Pagination";
import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { ActionTitleActions } from "@/components/actionTitle/ActionTitleActions";
import { useModals } from "@/features/modals/hooks";
import { useSearchProject } from "@/features/projects/SearchProject";
import { getCoworkerSettingsValue } from "@/features/workspace/customize/settings/CoworkerSettingsControl";
import { useTrackWidgetFilters } from "@/features/workspace/useTrackWidgetFilters";
import {
  ScrollContainer,
  WidgetContainer,
} from "@/features/workspace/widgets/Components";
import {
  type ClassificationDefinition,
  type InstallationQuotationStatus,
  type InstallationType,
  type NbaType,
  type OpportunityType,
  ProjectFilterDefault,
  type WidgetSetting,
  type WidgetSize,
  type WorkOrderStatus,
  type WorkOrderType,
  type hfb,
} from "@repo/api";
import { Text, plusIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { openNewProjectModal } from "@repo/ui";
import { isDefined, trackAnalytics } from "@repo/utils";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { CARD_HEIGHT_NORMAL } from "../constants";
import { Table } from "./Table";

const getSettings = (settings?: WidgetSetting) => {
  const { email, teamId, assignedToTeam } = getCoworkerSettingsValue(
    settings?.["COWORKER"]?.toString() ?? ""
  );

  const unitId =
    settings?.STORE !== undefined
      ? Array.isArray(settings.STORE)
        ? settings.STORE
        : [settings.STORE]
      : undefined;

  const createdDate = Array.isArray(settings?.CREATED_DATE)
    ? settings.CREATED_DATE.map((date) => new Date(date))
    : [];

  const updatedDate = Array.isArray(settings?.UPDATED_DATE)
    ? settings.UPDATED_DATE.map((date) => new Date(date))
    : [];

  const projectType =
    settings?.TYPE !== undefined ? (settings.TYPE as OpportunityType[]) : [];

  const department =
    settings?.DEPARTMENT !== undefined ? (settings.DEPARTMENT as hfb[]) : [];

  const projectStatus =
    settings?.STATUS !== undefined ? (settings.STATUS as string[]) : [];

  const projectValue =
    settings?.PROJECT_VALUE !== undefined
      ? (settings.PROJECT_VALUE as number[])
      : [];

  const customerType =
    settings?.CUSTOMER_TYPE !== undefined
      ? (settings.CUSTOMER_TYPE as Customer["customerType"][])
      : [];

  const preferredLanguage =
    settings?.PREFERRED_LANGUAGE !== undefined
      ? (settings.PREFERRED_LANGUAGE as string[])
      : [];

  const classification =
    settings?.CLASSIFICATION !== undefined
      ? (settings.CLASSIFICATION as ClassificationDefinition[])
      : [];

  const services =
    settings?.SERVICES !== undefined ? (settings.SERVICES as number[]) : [];

  const followUpConsent =
    settings?.FOLLOW_UP_CONSENT !== undefined
      ? Array.isArray(settings.FOLLOW_UP_CONSENT)
        ? (settings.FOLLOW_UP_CONSENT[0] as string)
        : (settings.FOLLOW_UP_CONSENT as string)
      : undefined;

  const promotionalConsent =
    settings?.PROMOTIONAL_CONSENT !== undefined
      ? Array.isArray(settings.PROMOTIONAL_CONSENT)
        ? (settings.PROMOTIONAL_CONSENT[0] as string)
        : (settings.PROMOTIONAL_CONSENT as string)
      : undefined;

  const tags = settings?.TAGS !== undefined ? (settings.TAGS as string[]) : [];

  const orderMethod =
    settings?.ORDER_METHOD !== undefined
      ? (settings.ORDER_METHOD as string[])
      : [];

  const orderStatus =
    settings?.ORDER_STATUS !== undefined
      ? (settings.ORDER_STATUS as string[])
      : [];

  const nbaTypes =
    settings?.NEXT_BEST_ACTIONS !== undefined
      ? (settings.NEXT_BEST_ACTIONS as NbaType[])
      : [];

  const bokaServices =
    settings?.BOKA_SERVICES !== undefined
      ? (settings.BOKA_SERVICES as string[])
      : [];

  const installationQuotationExists =
    settings?.INSTALLATION_QUOTATION !== undefined
      ? Array.isArray(settings.INSTALLATION_QUOTATION) &&
        settings.INSTALLATION_QUOTATION.includes("have_installation_quotation")
        ? true
        : false
      : undefined;

  const installationQuotationStatus =
    settings?.INSTALLATION_QUOTATION_STATUS !== undefined
      ? (settings.INSTALLATION_QUOTATION_STATUS as InstallationQuotationStatus[])
      : [];

  const workOrderStatus =
    settings?.WORKORDER_STATUS !== undefined
      ? (settings.WORKORDER_STATUS as WorkOrderStatus[])
      : [];

  const workOrderType =
    settings?.WORKORDER_TYPE !== undefined
      ? (settings.WORKORDER_TYPE as WorkOrderType[])
      : [];

  const installationType =
    settings?.INSTALLATION_TYPE !== undefined
      ? (settings.INSTALLATION_TYPE as InstallationType[])
      : [];

  const installationDate = Array.isArray(settings?.INSTALLATION_DATE)
    ? settings.INSTALLATION_DATE.map((date) => new Date(date))
    : [];

  const stageFilter =
    settings?.STAGE !== undefined ? (settings.STAGE as string[]) : [];

  const budgetFilter =
    settings?.BUDGET !== undefined ? (settings.BUDGET as number[]) : [];

  const expectedPurchaseDateFilter = Array.isArray(
    settings?.EXPECTED_PURCHASE_DATE
  )
    ? settings.EXPECTED_PURCHASE_DATE.map((date) => new Date(date))
    : [];

  return {
    unitId,
    email,
    teamId,
    assignedToTeam,
    createdDate,
    updatedDate,
    projectType,
    projectStatus,
    classification,
    services,
    orderStatus,
    projectValue,
    customerType,
    preferredLanguage,
    orderMethod,
    followUpConsent,
    promotionalConsent,
    department,
    tags,
    nbaTypes,
    bokaServices,
    installationQuotationExists,
    installationQuotationStatus,
    workOrderStatus,
    workOrderType,
    installationType,
    installationDate,
    stageFilter,
    budgetFilter,
    expectedPurchaseDateFilter,
  };
};

interface Props {
  title: string;
  settings?: WidgetSetting;
  size: WidgetSize;
}
export const ProjectsWidget = ({ title, settings, size }: Props) => {
  const { pushModal } = useModals();
  const navigate = useNavigate();

  const widgetSettings = useMemo(() => {
    return getSettings(settings);
  }, [settings]);

  const {
    result: projects,
    totalSize: resultTotalSize,
    size: resultSize,
    setSize: setResultSize,
    isValidating,
    actions,
    setOptions,
  } = useSearchProject();

  useTrackWidgetFilters("PROJECTS", isValidating, widgetSettings);

  const filters = useMemo(() => {
    const {
      unitId,
      email,
      teamId,
      assignedToTeam,
      createdDate,
      updatedDate,
      projectType,
      projectStatus,
      classification,
      services,
      orderStatus,
      customerType,
      projectValue,
      preferredLanguage,
      orderMethod,
      followUpConsent,
      promotionalConsent,
      department,
      tags,
      nbaTypes,
      bokaServices,
      installationQuotationExists,
      installationQuotationStatus,
      workOrderStatus,
      workOrderType,
      installationType,
      installationDate,
      stageFilter,
      budgetFilter,
      expectedPurchaseDateFilter,
    } = widgetSettings;

    return {
      ...ProjectFilterDefault,
      isWidgetSearch: true,
      coworkerFilter: teamId === undefined ? [email].filter(isDefined) : [],
      teamFilter: teamId,
      assignedToTeam,
      createdDate,
      updatedDate,
      opportunityType: projectType,
      status: projectStatus.filter(
        (status) => status !== "NO_ACTION_PLANNED" && status !== "COLD_PROJECT"
      ),
      businessUnit: unitId,
      orderStatus,
      classification,
      services,
      amount: projectValue,
      customerType,
      customerLocales: preferredLanguage,
      orderMethod,
      isNoActionPlanned: projectStatus.includes("NO_ACTION_PLANNED"),
      isColdProject: projectStatus.includes("COLD_PROJECT"),
      followUpConsentFilter: followUpConsent?.toLowerCase(),
      promotionalConsentFilter: promotionalConsent?.toLowerCase(),
      department,
      tags,
      bokaServices,
      installationQuotationExists,
      installationQuotationStatus,
      workOrderStatus,
      workOrderType,
      nbaTypes,
      installationType,
      installationDate,
      stageFilter,
      budgetFilter,
      expectedPurchaseDateFilter,
    };
  }, [widgetSettings]);

  useEffect(() => {
    setOptions((prev) => {
      if (prev?.isPinned === true) {
        return prev;
      }

      return {
        ...prev,
        isPinned: true,
      };
    });
    actions.setAllFilters(filters);
  }, [filters, setOptions, actions]);

  return (
    <WidgetContainer size={CARD_HEIGHT_NORMAL}>
      <ActionTitle
        title={`${title} (${resultTotalSize ?? 0})`}
        action={
          <ActionTitleActions
            actions={[
              {
                id: "create-new-project-action",
                text: t("label.new_projects"),
                title: t("label.new_projects"),
                icon: plusIcon,
                iconOnly: true,
                onClick: () => {
                  openNewProjectModal({
                    pushModal,
                    resetModal: true,
                    onProjectCreated(projectId) {
                      navigate(`/opportunity/${projectId}/`);
                      trackAnalytics("new_project_created");
                    },
                    onSelectProject(projectId) {
                      navigate(`/opportunity/${projectId}/`);
                      trackAnalytics("manage_project");
                    },
                  });
                },
              },
            ]}
          />
        }
      />

      {projects.length === 0 && <Text>{t("prompt.no_search_result")}</Text>}
      {projects.length !== 0 && (
        <>
          <ScrollContainer>
            <Table isLoading={isValidating} projects={projects} size={size} />
          </ScrollContainer>

          <CompactPagination
            // TODO: revisit and fix error
            setSize={setResultSize}
            size={resultSize}
            totalNumber={resultTotalSize ?? 0}
            currentNumber={projects.length}
          />
        </>
      )}
    </WidgetContainer>
  );
};
